import { useState, useEffect, useRef } from 'react';
import { ResponsiveAssignment } from '@core/assignments/types';

const DEBOUNCE_DELAY = 300;

const filterAssignments = (
  assignments: ResponsiveAssignment[],
  query: string
): ResponsiveAssignment[] => {
  if (!query.trim()) return assignments;
  const lower = query.toLowerCase();
  return assignments.filter((a) => a.title?.toLowerCase().includes(lower));
};

const useAssignmentSearch = (assignments: ResponsiveAssignment[]) => {
  const [inputValue, setInputValue] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleChange = (value: string) => {
    setInputValue(value);
    setIsSearching(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setDebouncedQuery(value);
      setIsSearching(false);
    }, DEBOUNCE_DELAY);
  };

  const handleClear = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setInputValue('');
    setDebouncedQuery('');
    setIsSearching(false);
  };

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    []
  );

  return {
    inputValue,
    isSearching,
    filteredAssignments: filterAssignments(assignments, debouncedQuery),
    hasActiveQuery: debouncedQuery.trim().length > 0,
    handleChange,
    handleClear,
  };
};

export default useAssignmentSearch;
